import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Truck, MapPin, Send, Phone, Building2, Navigation, Upload, X } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { TOW_STATUS, TOW_REASONS, fmtDate, fmtDistance } from "../lib/labels";
import MapView from "../components/MapView";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

export default function Cekici() {
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState(null);
  const [tows, setTows] = useState(null);
  const [towCity, setTowCity] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loc, setLoc] = useState(null);
  const [locError, setLocError] = useState("");
  const [form, setForm] = useState({ vehicleId: "", vin: "", brand: "", model: "", plate: "", city: "", district: "", address: "", note: "", tur: "Arıza", photos: [] });
  const fileRef = useRef(null);

  const load = () => {
    api.get("/my-vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/cekici/taleplerim").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  };
  useEffect(() => { load(); }, []);

  const useMyLocation = () => {
    setLocError("");
    if (!navigator.geolocation) { setLocError("Tarayıcınız konum özelliğini desteklemiyor."); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Konumunuz alındı"); },
      () => setLocError("Konum izni verilmedi. Adresi manuel olarak girebilirsiniz."),
      { timeout: 10000 });
  };

  const pickVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setForm((f) => ({
      ...f, vehicleId: vid,
      vin: v ? v.vin : "", brand: v ? v.brand : "", model: v ? v.model : "", plate: v ? v.plate || "" : "",
    }));
  };

  const searchTows = async () => {
    setTows(null);
    try {
      const params = { city: towCity };
      if (loc) { params.lat = loc.lat; params.lng = loc.lng; }
      const { data } = await api.get("/cekici/musait", { params });
      setTows(data);
    } catch (e) { toast.error(formatApiError(e)); setTows([]); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/cekici/talep", {
        vin: form.vin, brand: form.brand, model: form.model, plate: form.plate,
        city: form.city, district: form.district, address: form.address, note: form.note, tur: form.tur,
        lat: loc?.lat || 0, lng: loc?.lng || 0, photos: form.photos,
      });
      toast.success("Çekici talebiniz oluşturuldu, bölgedeki çekiciler bilgilendirildi");
      setForm({ vehicleId: "", vin: "", brand: "", model: "", plate: "", city: form.city, district: form.district, address: "", note: "", tur: "Arıza", photos: [] });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const towMarkers = (tows || []).filter((t) => t.lat && t.lng)
    .map((t) => ({ lat: t.lat, lng: t.lng, label: t.company_name, color: "#fb923c" }));

  return (
    <div data-testid="cekici-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6 text-orange-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Çekici Çağır</h1>
        </div>

        <form onSubmit={submit} data-testid="tow-request-form"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">Yeni Çekici Talebi</h2>

          <div>
            <label className={labelCls}>Talep Türü</label>
            <div className="flex flex-wrap gap-2">
              {TOW_REASONS.map((t) => (
                <button key={t} type="button" data-testid={`tow-reason-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setForm({ ...form, tur: t })}
                  className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                    form.tur === t ? "border-orange-400/60 bg-orange-500/15 text-orange-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Aracım</label>
              <select data-testid="tow-vehicle-select" value={form.vehicleId} onChange={(e) => pickVehicle(e.target.value)} className={inputCls}>
                <option value="">Manuel giriş / kayıtlı olmayan araç</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{`${v.brand} ${v.model} — ${v.plate || v.vin}`}</option>
                ))}
              </select></div>
            {!form.vehicleId && (
              <>
                <div className="sm:col-span-2"><label className={labelCls}>Şasi No / VIN (opsiyonel)</label>
                  <input data-testid="tow-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                    className={`${inputCls} font-mono-vin tracking-widest`} placeholder="17 haneli şasi numarası" /></div>
                <div><label className={labelCls}>Marka</label>
                  <input data-testid="tow-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Model</label>
                  <input data-testid="tow-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Plaka</label>
                  <input data-testid="tow-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} className={inputCls} /></div>
              </>
            )}
            <div><label className={labelCls}>İl *</label>
              <input data-testid="tow-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className={inputCls} placeholder="Kocaeli" /></div>
            <div><label className={labelCls}>İlçe</label>
              <input data-testid="tow-district" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}
                className={inputCls} placeholder="Çayırova" /></div>
            <div className="flex items-end">
              <button type="button" data-testid="tow-use-location" onClick={useMyLocation}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors w-full justify-center">
                <Navigation className="w-4 h-4" /> {loc ? "Konum Alındı ✓" : "Konumumu Kullan"}
              </button>
            </div>
            <div className="sm:col-span-2"><label className={labelCls}>Konum / Adres *</label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                <input data-testid="tow-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={`${inputCls} pl-10`} placeholder="Aracın bulunduğu açık adres" />
              </div>
              {locError && <p data-testid="tow-location-error" className="mt-1.5 text-xs text-amber-400">{locError}</p>}
            </div>
            {loc && (
              <div className="sm:col-span-2">
                <MapView center={[loc.lat, loc.lng]}
                  markers={[{ lat: loc.lat, lng: loc.lng, label: "Konumunuz" }, ...towMarkers]} height={220} />
              </div>
            )}
            <div className="sm:col-span-2"><label className={labelCls}>Açıklama</label>
              <textarea data-testid="tow-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className={inputCls} placeholder="Arıza durumu, ulaşım bilgisi..." /></div>
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={async (e) => {
                setUploading(true);
                try {
                  const paths = await uploadFiles([...e.target.files]);
                  setForm((f) => ({ ...f, photos: [...f.photos, ...paths] }));
                } catch (err) { toast.error(formatApiError(err)); }
                setUploading(false);
                e.target.value = "";
              }} />
            <button type="button" data-testid="tow-photo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Ekle"}
            </button>
            {form.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.photos.map((p, i) => (
                  <div key={p} className="relative">
                    <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button data-testid="tow-submit" type="submit" disabled={sending || uploading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 text-slate-950 font-bold text-sm hover:bg-orange-400 transition-all disabled:opacity-50">
            <Send className="w-4 h-4" /> {sending ? "Gönderiliyor..." : "Çekici Talebi Oluştur"}
          </button>
        </form>

        <div data-testid="tow-my-requests" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Taleplerim</h2>
          {requests === null ? (
            <div className="flex justify-center py-6"><div className="w-7 h-7 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-slate-500">Henüz çekici talebiniz yok.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => {
                const st = TOW_STATUS[r.status] || TOW_STATUS.bekliyor;
                return (
                  <div key={r.id} data-testid={`tow-request-${r.id}`}
                    className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-100">
                          {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.plate && ` — ${r.plate}`}
                          {r.tur && <span className="ml-2 text-xs text-orange-300">({r.tur})</span>}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)} • {r.city} • {r.address}</p>
                        {r.tow_company && <p className="text-xs text-cyan-300 mt-1">Üstlenen: {r.tow_company}{r.tow_phone && ` • ${r.tow_phone}`}</p>}
                      </div>
                      <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                    </div>
                    {r.photos && r.photos.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {r.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div data-testid="tow-available" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100">Yakındaki Müsait Çekiciler</h2>
          <div className="mt-3 flex gap-2">
            <input data-testid="tow-search-city" value={towCity} onChange={(e) => setTowCity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchTows())}
              className={inputCls} placeholder="Şehir girin (örn. İstanbul)" />
            <button data-testid="tow-search-button" onClick={searchTows}
              className="px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-bold text-sm hover:bg-cyan-300 transition-all whitespace-nowrap">
              Listele
            </button>
          </div>
          {tows !== null && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tows.length === 0 ? (
                <p className="text-sm text-slate-500 sm:col-span-2">Bu bölgede müsait çekici bulunamadı.</p>
              ) : tows.map((t) => (
                <div key={t.id} data-testid={`tow-card-${t.id}`}
                  className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
                  <p className="font-semibold text-slate-100 flex items-center gap-2"><Building2 className="w-4 h-4 text-orange-300" /> {t.company_name}</p>
                  <p className="text-xs text-slate-400 mt-1">{t.city}{t.district && ` / ${t.district}`}{t.driver_name && ` • ${t.driver_name}`}</p>
                  {t.mesafe_km != null && (
                    <p data-testid={`tow-distance-${t.id}`} className="text-xs text-cyan-300 mt-1 font-semibold">{fmtDistance(t.mesafe_km)}</p>
                  )}
                  {t.note && <p className="text-xs text-slate-500 mt-1">{t.note}</p>}
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> İletişim, talep kabulünde paylaşılır</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
